import React, { useState, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { Modal } from '../components/Modal';
import ReminderForm from '../components/Forms/Leads/ReminderForm';
import LeadDetailsSidebar from '@/components/LeadDetails/LeadDetailsSidebar';
import { getPersonalEvents } from '@/actions/data-fetch-activities';
import { useUsers } from '@/contexts/UsersContext';
import { Lead } from '@/types/leads/Table';
import {
  getDataAccess,
  canCreateActivities,
  canViewUser,
  shouldShowTeamMembers,
  DATA_ACCESS
} from '@/utils/permissions';
import {
  Calendar,
  Clock,
  Tag,
  Plus,
  CalendarDays,
  RefreshCw,
  Menu,
  X,
  CheckCircle
} from 'lucide-react';

// Updated interface to match API response
export interface Event {
  event_id?: number;
  uuid?: string;
  event_title: string;
  event_description?: string | null;
  db_start: string;
  db_end?: string;
  alert_before: string;
  lead_uuid?: string | null;
  lead?: {
    lead_id: number;
    uuid: string;
    name: string | null;
    email: string | null;
    phone: string;
    status: number;
  };
  user_uuid?: string | null;
  user_name?: string | null;
  user_email?: string | null;
  user?: {
    userId: number;
    uuid: string;
    userName: string;
    userEmail: string;
  } | null;
  closed?: boolean;
  event_created_at?: string;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end?: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  classNames?: string[];
  extendedProps?: {
    originalEvent: Event;
  };
}

interface UserOption {
  value: string;
  label: string;
  email?: string;
  role?: string;
}

const ActivitiesPage: React.FC = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  // Lead modal state
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'personal' | 'leads' | 'today' | 'upcoming'>('all');
  const [selectedUser] = useState<string>('all');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [visibleUsers, setVisibleUsers] = useState<Set<string>>(new Set());
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [dateRange, setDateRange] = useState<{ from: string; to: string } | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    personal: 0,
    leads: 0,
    today: 0,
    upcoming: 0
  });

  // Get users from global context (no duplicate API call!)
  const { users: contextUsers, activeUsers } = useUsers();

  // Transform activeUsers to dropdown format
  const activeUsersForDropdown: UserOption[] = activeUsers.map(u => ({
    value: u.uuid,
    label: u.userName,
    email: u.userEmail,
    role: u.role?.role_name
  }));

  // Get current user and permissions
  const dataAccess = getDataAccess();
  const canCreate = canCreateActivities();
  const showTeamMembers = shouldShowTeamMembers();

  // Process users from context when they change
  useEffect(() => {
    if (contextUsers && contextUsers.length > 0 && showTeamMembers) {
      const users = contextUsers.map((user: any) => ({
        uuid: user.uuid,
        userId: user.userId,
        name: user.userName,
        email: user.userEmail,
        team_id_fk: user.team_id_fk,
        department_id_fk: user.department_id_fk,
        eventCount: 0 // Will be updated after events are fetched
      }));

      // Filter users based on data access level
      const filteredUsers = users.filter((user: any) =>
        canViewUser(user.userId || user.uuid, user.team_id_fk, user.department_id_fk)
      );

      setAllUsers(filteredUsers);
      console.log('✅ Using cached users from context:', filteredUsers.length);
      console.log('Data Access Level:', dataAccess);

      // Initialize all visible users
      const allUserUuids = new Set(filteredUsers.map((u: any) => u.uuid));
      setVisibleUsers(allUserUuids);
    }
  }, [contextUsers, showTeamMembers, dataAccess]);

  // Fetch when date range changes (driven by datesSet from FullCalendar)
  useEffect(() => {
    if (dateRange) fetchEvents(dateRange.from, dateRange.to);
  }, [dateRange]);

  const fetchEvents = async (dateFrom?: string, dateTo?: string) => {
    try {
      setIsLoading(true);
      const response = await getPersonalEvents(dateFrom, dateTo);
      console.log('Events API Response:', response);

      // Handle the actual API response structure
      const apiData: any = response.data || response;
      const eventsArray = Array.isArray(apiData) ? apiData : apiData.data || [];

      // Calculate stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const statsData = {
        total: eventsArray.length,
        personal: eventsArray.filter((e: Event) => !e.lead_uuid).length,
        leads: eventsArray.filter((e: Event) => e.lead_uuid).length,
        today: eventsArray.filter((e: Event) => {
          const eventDate = new Date(e.db_start);
          eventDate.setHours(0, 0, 0, 0);
          return eventDate.getTime() === today.getTime();
        }).length,
        upcoming: eventsArray.filter((e: Event) => {
          const eventDate = new Date(e.db_start);
          return eventDate > new Date();
        }).length
      };
      setStats(statsData);

      // Update user event counts and sort by event count (users with events on top)
      setAllUsers(prevUsers => {
        const usersWithCounts = prevUsers.map(user => ({
          ...user,
          eventCount: eventsArray.filter((e: Event) => (e.user_uuid || (e.user as any)?.uuid) === user.uuid).length
        }));

        // Sort: Users with events first (by event count descending), then users without events (alphabetically)
        return usersWithCounts.sort((a, b) => {
          if (a.eventCount === 0 && b.eventCount === 0) {
            return a.name.localeCompare(b.name); // Both have no events - sort alphabetically
          }
          if (a.eventCount === 0) return 1; // a has no events - move down
          if (b.eventCount === 0) return -1; // b has no events - move down
          return b.eventCount - a.eventCount; // Both have events - sort by count descending
        });
      });

      // Transform events for calendar
      const calendarEvents = eventsArray.map((event: Event) => {
        const isPersonalEvent = !event.lead_uuid;
        const isClosed = event.closed;

        // Color scheme based on event type and status
        let backgroundColor = '#3b82f6'; // Default blue
        let borderColor = '#2563eb';

        if (isClosed) {
          backgroundColor = '#6b7280'; // Gray for closed
          borderColor = '#4b5563';
        } else if (!isPersonalEvent) {
          backgroundColor = '#10b981'; // Green for lead events
          borderColor = '#059669';
        }

        return {
          id: event.uuid || String(event.event_id) || Math.random().toString(),
          title: event.event_title,
          start: event.db_start,
          end: event.db_end || event.db_start,
          backgroundColor,
          borderColor,
          textColor: '#ffffff',
          classNames: [isClosed ? 'event-closed' : 'event-active'],
          extendedProps: {
            originalEvent: event
          }
        };
      });

      setEvents(calendarEvents);
    } catch (err) {
      console.error("Error fetching events:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateClick = (info: any) => {
    // Only allow creating events if user has permission
    if (!canCreate) {
      console.warn('User does not have permission to create events');
      return;
    }

    const newEvent: Event = {
      event_title: '',
      db_start: info.dateStr,
      alert_before: '0 min',
    };
    setSelectedEvent(newEvent);
    setIsReminderModalOpen(true);
  };

  const handleEventClick = async (info: any) => {
    const originalEventData = info.event.extendedProps?.originalEvent;
    if (originalEventData) {
      // Check if this is a lead event or personal event
      if (originalEventData.lead_uuid) {
        // Lead event — open reminder/edit form
        setSelectedEvent(originalEventData);
        setIsReminderModalOpen(true);
      } else {
        // This is a personal event - open reminder form
        setSelectedEvent(originalEventData);
        setIsReminderModalOpen(true);
      }
    }
  };

  const closeModal = () => {
    setIsReminderModalOpen(false);
    setSelectedEvent(null);
  };

  const closeLeadModal = () => {
    setIsLeadModalOpen(false);
    setSelectedLead(null);
  };

  const refreshData = useCallback(async () => {
    await fetchEvents(dateRange?.from, dateRange?.to);
  }, [dateRange]);

  const filteredEvents = events.filter(event => {
    const originalEvent = event.extendedProps?.originalEvent;
    if (!originalEvent) return true;

    // Apply role-based access control
    // Check if the current user can view events from this event's owner
    if (originalEvent.user) {
      const eventOwner = allUsers.find(u => u.uuid === originalEvent.user?.uuid);
      if (eventOwner) {
        const canView = canViewUser(
          eventOwner.userId || eventOwner.uuid,
          eventOwner.team_id_fk,
          eventOwner.department_id_fk
        );
        if (!canView) return false;
      }
    } else if (dataAccess !== DATA_ACCESS.ALL) {
      // If event has no user and data access is restricted, filter it out
      // unless it's the current user's personal event
      return false;
    }

    // Apply filter
    if (filter === 'personal' && originalEvent.lead_uuid) return false;
    if (filter === 'leads' && !originalEvent.lead_uuid) return false;

    // New filters for today and upcoming
    if (filter === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const eventDate = new Date(originalEvent.db_start);
      eventDate.setHours(0, 0, 0, 0);
      if (eventDate.getTime() !== today.getTime()) return false;
    }

    if (filter === 'upcoming') {
      const eventDate = new Date(originalEvent.db_start);
      const now = new Date();
      if (eventDate <= now) return false;
    }

    // Apply user visibility filter (API returns flat user_uuid)
    if (showTeamMembers && visibleUsers.size > 0) {
      const eventUserUuid = originalEvent.user_uuid || originalEvent.user?.uuid;
      if (eventUserUuid && !visibleUsers.has(eventUserUuid)) return false;
    }

    // Apply user filter
    if (selectedUser !== 'all' && originalEvent.user?.uuid !== selectedUser) return false;

    return true;
  });

  // Use the allUsers from API instead of extracting from events

  const toggleUserVisibility = (userUuid: string) => {
    const newVisibleUsers = new Set(visibleUsers);
    if (newVisibleUsers.has(userUuid)) {
      newVisibleUsers.delete(userUuid);
    } else {
      newVisibleUsers.add(userUuid);
    }
    setVisibleUsers(newVisibleUsers);
  };

  return (
    <div className="h-full flex bg-gray-50 dark:bg-gray-900">
      {/* Sidebar - Google Calendar Style */}
      <div className={`bg-white dark:bg-gray-50 border-r border-gray-200 dark:border-gray-300 transition-all duration-300 ${
        sidebarOpen ? 'w-72' : 'w-16'
      } flex-shrink-0 flex flex-col`}>

        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-300">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div className="flex items-center space-x-3">
                <Calendar className="w-6 h-6 text-brandColor" />
                <h2 className="text-lg font-medium text-gray-900">Activities</h2>
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {sidebarOpen ? (
                <X className="w-5 h-5 text-gray-600" />
              ) : (
                <Menu className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {sidebarOpen && (
          <div className="flex-1 overflow-hidden flex flex-col">

            {/* Clickable Stats */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-300">
              <div className="space-y-1">
                <button
                  onClick={() => setFilter('all')}
                  className={`w-full flex items-center justify-between text-sm p-2 rounded-lg transition-colors ${
                    filter === 'all' ? 'bg-green-100 text-brandColor' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="flex items-center">
                    <CalendarDays className="w-4 h-4 mr-2 text-brandColor" />
                    Total
                  </span>
                  <span className="font-medium">({stats.total})</span>
                </button>
                <button
                  onClick={() => setFilter('personal')}
                  className={`w-full flex items-center justify-between text-sm p-2 rounded-lg transition-colors ${
                    filter === 'personal' ? 'bg-purple-100 text-purple-700' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="flex items-center">
                    <Tag className="w-4 h-4 mr-2 text-purple-600" />
                    Personal
                  </span>
                  <span className="font-medium">({stats.personal})</span>
                </button>
                <button
                  onClick={() => setFilter('today')}
                  className={`w-full flex items-center justify-between text-sm p-2 rounded-lg transition-colors ${
                    filter === 'today' ? 'bg-orange-100 text-orange-700' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-orange-600" />
                    Today
                  </span>
                  <span className="font-medium">({stats.today})</span>
                </button>
                <button
                  onClick={() => setFilter('upcoming')}
                  className={`w-full flex items-center justify-between text-sm p-2 rounded-lg transition-colors ${
                    filter === 'upcoming' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-indigo-600" />
                    Upcoming
                  </span>
                  <span className="font-medium">({stats.upcoming})</span>
                </button>
              </div>
            </div>


            {/* Users List with Scroll - Only show if user has permission */}
            {showTeamMembers && (
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="p-4 pb-2">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-medium text-gray-700">Team Members</h3>
                      <p className="text-xs text-gray-400">counts for current view</p>
                    </div>
                    <button
                      onClick={refreshData}
                      className="p-1 rounded hover:bg-gray-100 transition-colors"
                      title="Refresh"
                    >
                      <RefreshCw className={`w-4 h-4 text-gray-500 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-4 pb-4">
                  <div className="space-y-1">
                    {allUsers.length > 0 ? (
                      allUsers.map((user: any) => (
                        <div
                          key={user.uuid}
                          className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer group"
                          onClick={() => toggleUserVisibility(user.uuid)}
                        >
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                              visibleUsers.has(user.uuid) ? 'bg-green-500' : 'bg-gray-300'
                            }`}></div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {user.name}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {user.email}
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 ml-2">
                            ({user.eventCount || 0})
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-gray-500 text-center py-4">
                        No team members found
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Legend */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-300">
              <h3 className="text-xs font-medium text-gray-600 uppercase mb-2">Legend</h3>
              <div className="space-y-2 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-brandColor rounded"></div>
                  <span className="text-gray-600">Personal Events</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-600 rounded"></div>
                  <span className="text-gray-600">Lead Events</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Calendar Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Sticky Header */}
        <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
          <div className="px-6 py-3">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold text-gray-900">Activities</h1>
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-500">
                  {filteredEvents.length} events
                </div>
                {canCreate && (
                  <button
                    onClick={() => {
                      // Format current date/time in local timezone for datetime-local input
                      const now = new Date();
                      const year = now.getFullYear();
                      const month = String(now.getMonth() + 1).padStart(2, '0');
                      const day = String(now.getDate()).padStart(2, '0');
                      const hours = String(now.getHours()).padStart(2, '0');
                      const minutes = String(now.getMinutes()).padStart(2, '0');
                      const localDatetime = `${year}-${month}-${day}T${hours}:${minutes}`;

                      const newEvent: Event = {
                        event_title: '',
                        db_start: localDatetime,
                        alert_before: '0 min',
                      };
                      setSelectedEvent(newEvent);
                      setIsReminderModalOpen(true);
                    }}
                    className="px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brandColor transition-colors flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Create Event</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Container */}
        <div className="flex-1 p-4 min-h-0">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm h-full">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-brandColor mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Loading events...</p>
                </div>
              </div>
            ) : (
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                initialView="dayGridMonth"
                events={filteredEvents}
                datesSet={(info) => {
                  const from = info.startStr.slice(0, 10);
                  const to = info.endStr.slice(0, 10);
                  setDateRange(prev => (prev?.from === from && prev?.to === to ? prev : { from, to }));
                }}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                selectable={true}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth',
                }}
                views={{
                  listMonth: {
                    type: 'listMonth',
                    buttonText: 'List',
                  }
                }}
                eventContent={(eventInfo) => {
                  const originalEvent = eventInfo.event.extendedProps?.originalEvent;
                  const formatTime = (date: Date | null) => {
                    if (!date) return '';
                    return date.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  };

                  const agentName = originalEvent?.user_name || originalEvent?.user?.userName;

                  return (
                    <div className="p-1 overflow-hidden">
                      <div className="text-xs font-semibold truncate">
                        {formatTime(eventInfo.event.start)} {eventInfo.event.title}
                      </div>
                      {originalEvent?.lead_name && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs opacity-80 truncate flex-1">{originalEvent.lead_name}</span>
                          {originalEvent?.lead_uuid && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/lead/${originalEvent.lead_uuid}`, '_blank');
                              }}
                              className="opacity-80 hover:opacity-100 shrink-0"
                              title="Open lead in new tab"
                            >
                              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </button>
                          )}
                        </div>
                      )}
                      {showTeamMembers && agentName && (
                        <div className="text-xs opacity-90 truncate font-medium" style={{ color: 'rgba(255,255,255,0.95)' }}>
                          👤 {agentName}
                        </div>
                      )}
                    </div>
                  );
                }}
                height="calc(100vh - 140px)"
                slotMinTime="00:00:00"
                slotMaxTime="24:00:00"
                expandRows={true}
                dayMaxEvents={4}
                moreLinkText="more"
                eventDisplay="block"
                aspectRatio={1.35}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modern Event Modal */}
      <Modal
        isOpen={isReminderModalOpen}
        onClose={closeModal}
        size="lg"
        height="auto"
        position="center"
        className="max-w-2xl"
      >
        {selectedEvent && (
          <ReminderForm
            onClose={closeModal}
            onCancel={closeModal}
            selectedItemsActivities={[selectedEvent]}
            isActivity={true}
            showDelete={true}
            refreshData={refreshData}
          />
        )}
      </Modal>

      {/* Lead Details Sidebar for Lead Events */}
      <LeadDetailsSidebar
        isOpen={isLeadModalOpen}
        onClose={closeLeadModal}
        lead={selectedLead}
        activeUsersForDropdown={activeUsersForDropdown}
        onUpdate={refreshData}
      />
    </div>
  );
};

export default ActivitiesPage;